import { NotebookDetailsResponse } from "../interfaces/notebook-details-response";
import { NotebookDisplayed } from '../interfaces/notebook-displayed';
import { NOTEBOOK_STATUS_LABELS } from "../config/notebook-config";

export function mapNotebookDetailsToDisplayed(notebook: Partial<NotebookDetailsResponse>): NotebookDisplayed {
  const creationDateFormatted = notebook.createdAt ? formatDateTime(notebook.createdAt) : '';
  const updatedAtFormatted = notebook.updatedAt ? formatDateTime(notebook.updatedAt) : '';
  const restartDateFormatted = notebook.status?.lastStartedAt ? formatDateTime(notebook.status.lastStartedAt) : '';
  const stopDateFormatted = notebook.status?.lastStoppedAt ? formatDateTime(notebook.status.lastStoppedAt) : '';

  return {
    id: notebook.id ?? '',
    name: notebook.spec?.name ?? '',
    environment: notebook.spec?.env?.frameworkId ?? '',
    version: notebook.spec?.env?.frameworkVersion ?? '',
    cpu: notebook.spec?.resources?.cpu ?? 0,
    gpu: notebook.spec?.resources?.gpu ?? 0,
    runtime: formatRuntime(notebook.status?.duration ?? 0),
    status: formatStatus(notebook.status?.info?.code),
    accessUrl: notebook.status?.url ?? '',
    ram: notebook.spec?.resources?.memory
    ? Math.floor(parseInt(notebook.spec.resources.memory, 10) / (1024 ** 3))
    : 0,
    storage: notebook.spec?.resources?.ephemeralStorage
    ? Math.floor(parseInt(notebook.spec.resources.ephemeralStorage, 10)/ (1024 ** 3))
    : 0,
    privacy: notebook.spec?.unsecureHttp ? 'public' : 'private',
    creationDate: creationDateFormatted,
    timeline: [
      { label: 'Creation date', value: creationDateFormatted },
      ...(restartDateFormatted ? [{ label: 'Restart date', value: restartDateFormatted }] : []),
      ...(stopDateFormatted ? [{ label: 'Stop date', value: stopDateFormatted }] : []),
    ],
    flavor: notebook.spec?.resources?.flavor ?? '',
    gpuBrand: notebook.spec?.resources?.gpuBrand ?? '',
    gpuModel: notebook.spec?.resources?.gpuModel ?? '',
    cpuBrandModel: 'INTEL CPU VCORES',
    publicNetwork: notebook.spec?.resources?.publicNetwork
      ? formatNetworkSpeed(parseInt(notebook.spec.resources.publicNetwork, 10))
      : '',
    workspace: {
      storageFree: notebook.status?.workspace.storageFree ?? 0,
      storageUsed: notebook.status?.workspace.storageUsed ?? 0,
    },
 
    labels: notebook.spec?.labels ?? null,

  };
}

function formatRuntime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function formatStatus(code?: string): string {
  if (!code) return '';
  const clean = code.replace(/^NOTEBOOK_/, '').toLowerCase();
  const label = NOTEBOOK_STATUS_LABELS[clean] || clean;
  return capitalize(label);
}
  
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'long' }); // April
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

function formatNetworkSpeed(bytesPerSecond: number): string {
  const GIGABYTE = 1_073_741_824; 
  const MEGABYTE = 1_048_576;    
  if (bytesPerSecond >= GIGABYTE) {
    const gbps = bytesPerSecond / GIGABYTE;
    return `${gbps.toFixed(2)} GB/s`;
  } else if (bytesPerSecond >= MEGABYTE) {
    const mbps = bytesPerSecond / MEGABYTE;
    return `${mbps.toFixed(2)} MB/s`;
  } else {
    const kbps = bytesPerSecond / 1024;
    return `${kbps.toFixed(2)} KB/s`;
  }
}

export function convertBytesToGiB(value: number): number {
  return value / 1073741824;
}


